import { useMemo, useState } from "react";
import { ArrowLeft, Check, Download, Eye, Printer } from "lucide-react";
import type { CustomerServiceResource } from "../data/customerServiceResources";
import type { ResourceDocumentState } from "../hooks/useLearningState";

const emptyDocument = (resource: CustomerServiceResource): ResourceDocumentState => ({
  resourceId: resource.id,
  resourceVersion: resource.version,
  academicVersion: resource.academicVersion,
  mode: resource.modes?.[0],
  values: {},
  status: "not-started",
  updatedAt: "",
});

export function CustomerServiceResourceWorkspace({
  resource,
  document: storedDocument,
  onChange,
  onClose,
  saveStatus,
}: {
  resource: CustomerServiceResource;
  document?: ResourceDocumentState;
  onChange: (next: ResourceDocumentState) => void;
  onClose: () => void;
  saveStatus: "idle" | "saving" | "saved" | "error";
}) {
  const value = storedDocument ?? emptyDocument(resource);
  const [showModel, setShowModel] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const requiredFields = resource.fields.filter((field) => field.required);
  const completed = useMemo(
    () => requiredFields.filter((field) => value.values[field.id]?.trim()).length,
    [requiredFields, value.values],
  );

  const update = (fieldId: string, text: string) =>
    onChange({
      ...value,
      values: { ...value.values, [fieldId]: text },
      status: "in-progress",
      updatedAt: new Date().toISOString(),
    });

  const markComplete = () => {
    const missing = requiredFields
      .filter((field) => !value.values[field.id]?.trim())
      .map((field) => field.label);
    setErrors(missing);
    if (!missing.length)
      onChange({ ...value, status: "complete", updatedAt: new Date().toISOString() });
  };

  const download = () => {
    const lines = [
      `# ${resource.title}`,
      "Course: PA-CS-201",
      `Resource: ${resource.id} - v${resource.version}`,
      `Academic compatibility: ${resource.academicVersion}`,
      `Mode: ${value.mode ?? "standard"}`,
      `Last saved: ${value.updatedAt || "Not saved"}`,
      "",
      ...resource.fields.map(
        (field) => `## ${field.label}\n${value.values[field.id] || ""}`,
      ),
    ];
    const url = URL.createObjectURL(
      new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" }),
    );
    const link = window.document.createElement("a");
    link.href = url;
    link.download = `PA-CS-201-${resource.id}-v${resource.version}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="resource-workspace" id="resource-workspace">
      <header>
        <button className="text-button" onClick={onClose}>
          <ArrowLeft /> Back to lesson
        </button>
        <div>
          <p className="eyebrow">Module resource - Tier {resource.tier}</p>
          <h1>{resource.title}</h1>
          <p>{resource.purpose}</p>
        </div>
        <span className={`save-indicator ${saveStatus}`} role="status">
          {saveStatus === "error"
            ? "Not saved - check connection"
            : saveStatus === "saving"
              ? "Saving..."
              : value.updatedAt
                ? `Saved ${new Date(value.updatedAt).toLocaleString()}`
                : "Not started"}
        </span>
      </header>
      <section className="resource-instructions" aria-labelledby="resource-instructions-title">
        <h2 id="resource-instructions-title">How to use this resource</h2>
        <p>{resource.instructions}</p>
        <dl>
          <div><dt>Resource ID</dt><dd>{resource.id}</dd></div>
          <div><dt>Version</dt><dd>{resource.version}</dd></div>
          <div><dt>Academic compatibility</dt><dd>{resource.academicVersion}</dd></div>
          <div><dt>Practical category</dt><dd>{resource.practicalCategory}</dd></div>
        </dl>
        {resource.modes && (
          <fieldset>
            <legend>Working mode</legend>
            {resource.modes.map((mode) => (
              <label key={mode}>
                <input
                  type="radio"
                  name="resource-mode"
                  checked={value.mode === mode}
                  onChange={() =>
                    onChange({
                      ...value,
                      mode,
                      status: "in-progress",
                      updatedAt: new Date().toISOString(),
                    })
                  }
                />
                {mode === "small-business" ? "Minimum / small-business" : "Expanded company"}
              </label>
            ))}
          </fieldset>
        )}
      </section>
      <section className="resource-model">
        <button aria-expanded={showModel} onClick={() => setShowModel((current) => !current)}>
          <Eye /> {showModel ? "Hide model" : "View model / worked example"}
        </button>
        {showModel && (
          <aside>
            <p className="eyebrow">Model - not learner work</p>
            <h2>{resource.modelTitle}</h2>
            <p>{resource.modelSummary}</p>
            <small>Use this to judge structure and professional reasoning. Do not present it as your own work.</small>
          </aside>
        )}
      </section>
      {errors.length > 0 && (
        <section className="resource-errors" role="alert" aria-labelledby="resource-errors-title">
          <h2 id="resource-errors-title">Complete the required fields</h2>
          <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </section>
      )}
      <form
        className={`resource-form ${resource.id === "company-service-playbook" ? "playbook-form" : ""}`}
        onSubmit={(event) => { event.preventDefault(); markComplete(); }}
      >
        {resource.fields.map((field, index) => {
          const area = resource.id === "company-service-playbook" && index % 9 === 0
            ? field.label.split(" - ")[0]
            : null;
          const helpId = `${resource.id}-${field.id}-help`;
          return (
            <div className="resource-field" key={field.id}>
              {area && <h2>{area}</h2>}
              <label htmlFor={`${resource.id}-${field.id}`}>
                {field.label}{field.required && <span>Required</span>}
              </label>
              {field.help && <small id={helpId}>{field.help}</small>}
              {field.kind === "select" ? (
                <select id={`${resource.id}-${field.id}`} value={value.values[field.id] ?? ""} onChange={(event) => update(field.id, event.target.value)}>
                  <option value="">Select</option>
                  {field.options?.map((option) => <option key={option}>{option}</option>)}
                </select>
              ) : field.kind === "text" ? (
                <input id={`${resource.id}-${field.id}`} value={value.values[field.id] ?? ""} aria-describedby={field.help ? helpId : undefined} onChange={(event) => update(field.id, event.target.value)} />
              ) : (
                <textarea id={`${resource.id}-${field.id}`} value={value.values[field.id] ?? ""} aria-describedby={field.help ? helpId : undefined} onChange={(event) => update(field.id, event.target.value)} />
              )}
            </div>
          );
        })}
        <div className="resource-actions">
          <span>{completed} of {requiredFields.length} required fields completed</span>
          <button type="submit"><Check /> Mark resource complete</button>
        </div>
      </form>
      <footer className="resource-export">
        <button onClick={download}><Download /> Download editable Markdown</button>
        <button onClick={() => window.print()}><Printer /> Print / Save as PDF</button>
        <p>{resource.print.pageTitle} - {resource.print.orientation}</p>
      </footer>
    </main>
  );
}
